// ==================== REMINDER BANNER COMPONENT ====================
/**
 * SAFE PATCH: Banner de lembretes de agendamento para o cliente.
 * Exibe notificações de lembretes pendentes com ações de confirmação.
 */
import React, { useState, useEffect } from 'react';
import { Bell, Check, Calendar, X, ChevronRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReminderBanner({ onReminderCountChange }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/reminders/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReminders(data.reminders || []);
        if (onReminderCountChange) {
          onReminderCountChange(data.pending_count || 0);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar lembretes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (appointmentId, action) => {
    setRespondingTo(appointmentId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reminders/${appointmentId}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });

      const data = await response.json();

      if (response.ok) {
        // Remover o lembrete respondido da lista
        setReminders(prev => prev.filter(r => r.appointment_id !== appointmentId));
        
        // Mostrar toast de sucesso
        if (action === 'confirm') {
          toast.success('✅ Presença confirmada!', {
            description: 'O prestador foi notificado. Até lá!'
          });
        } else if (action === 'reschedule') {
          toast.info('🔄 Solicitação enviada', {
            description: 'O prestador entrará em contato para reagendar.'
          });
        } else if (action === 'cancel') {
          toast.warning('❌ Solicitação de cancelamento enviada', {
            description: 'O prestador foi notificado.'
          });
        }

        // Atualizar contador
        if (onReminderCountChange) {
          onReminderCountChange(reminders.length - 1);
        }
      } else {
        toast.error('Erro ao processar resposta', {
          description: data.detail || 'Tente novamente.'
        });
      }
    } catch (error) {
      console.error('Erro ao responder lembrete:', error);
      toast.error('Erro de conexão', {
        description: 'Verifique sua internet e tente novamente.'
      });
    } finally {
      setRespondingTo(null);
    }
  };

  if (loading || reminders.length === 0) {
    return null;
  }

  const displayReminders = showAll ? reminders : reminders.slice(0, 1);

  return (
    <div className="space-y-3">
      {displayReminders.map((reminder) => (
        <div 
          key={reminder.id}
          className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 shadow-sm"
        >
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Bell className="w-5 h-5 text-amber-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-amber-900">
                  {reminder.title || '⏰ Lembrete de lavagem'}
                </h3>
                <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-medium rounded-full">
                  {reminder.reminder_type === '24h' ? '24h antes' : '2h antes'}
                </span>
              </div>
              
              <p className="text-sm text-amber-700 mt-1">
                {reminder.message}
              </p>

              {/* Detalhes */}
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-amber-600">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {reminder.data?.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {reminder.data?.time}
                </span>
              </div>

              {/* Ações */}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  size="sm"
                  onClick={() => handleResponse(reminder.appointment_id, 'confirm')}
                  disabled={respondingTo === reminder.appointment_id}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`confirm-reminder-${reminder.appointment_id}`}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Confirmar
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleResponse(reminder.appointment_id, 'reschedule')}
                  disabled={respondingTo === reminder.appointment_id}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid={`reschedule-reminder-${reminder.appointment_id}`}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Reagendar
                </Button>
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleResponse(reminder.appointment_id, 'cancel')}
                  disabled={respondingTo === reminder.appointment_id}
                  className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                  data-testid={`cancel-reminder-${reminder.appointment_id}`}
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Ver mais */}
      {reminders.length > 1 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full py-2 text-sm text-amber-600 hover:text-amber-700 flex items-center justify-center gap-1 hover:bg-amber-50 rounded-lg transition-colors"
        >
          Ver mais {reminders.length - 1} lembrete(s)
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {showAll && reminders.length > 1 && (
        <button
          onClick={() => setShowAll(false)}
          className="w-full py-2 text-sm text-slate-500 hover:text-slate-600 flex items-center justify-center gap-1 hover:bg-slate-50 rounded-lg transition-colors"
        >
          Mostrar menos
        </button>
      )}
    </div>
  );
}
